import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Typography, Button, IconButton, Paper,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Divider, Dialog, DialogActions, TextField,
  Alert, Tooltip,
} from "@mui/material";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon     from "@mui/icons-material/CancelOutlined";
import PersonOutlineIcon      from "@mui/icons-material/PersonOutline";
import CalendarTodayIcon      from "@mui/icons-material/CalendarToday";
import BusinessIcon           from "@mui/icons-material/Business";
import EditNoteIcon           from "@mui/icons-material/EditNote";
import Inventory2Icon         from "@mui/icons-material/Inventory2";
import NotesIcon              from "@mui/icons-material/Notes";
import WarningAmberIcon       from "@mui/icons-material/WarningAmber";
import CheckCircleIcon        from "@mui/icons-material/CheckCircle";
import PriorityHighIcon       from "@mui/icons-material/PriorityHigh";
import ShoppingCartIcon       from "@mui/icons-material/ShoppingCart";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import api from "../../../services/api";

/* ─── Design Tokens ─────────────────────────────────────────────────────── */
const C = {
  primary:   "#2563EB", primaryBg: "#EFF6FF",
  surface:   "#FFFFFF", surfaceAlt: "#F8FAFC",
  border:    "#E2E8F0", borderHov:  "#CBD5E1",
  text1:     "#0F172A", text2:      "#475569", text3: "#94A3B8",
  success:   "#16A34A", successBg:  "#F0FDF4", successBdr: "#BBF7D0",
  warn:      "#D97706", warnBg:     "#FFFBEB", warnBdr:    "#FDE68A",
  danger:    "#DC2626", dangerBg:   "#FEF2F2", dangerBdr:  "#FECACA",
  infoBdr:   "#BFDBFE",
  neutral:   "#64748B", neutralBg:  "#F1F5F9", neutralBdr: "#CBD5E1",
};

const inputBase = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", backgroundColor: C.surface, fontSize: "0.82rem",
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.borderHov },
    "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: "1.5px" },
  },
};

const thSx = {
  fontSize: "0.72rem", fontWeight: 700, color: C.text3,
  borderBottom: `1px solid ${C.border}`, py: "10px", px: "14px",
  textTransform: "uppercase", letterSpacing: "0.04em",
  whiteSpace: "nowrap", backgroundColor: "transparent",
};
const tdSx = { py: "10px", px: "14px", borderBottom: `1px solid ${C.border}`, verticalAlign: "middle" };

/* ─── Status Chip ── */
const STATUS_CFG = {
  pending:      { label: "Pending",      color: C.warn,    bg: C.warnBg,    border: C.warnBdr,    dot: "#F59E0B" },
  dibeli:       { label: "Dibeli",       color: C.success, bg: C.successBg, border: C.successBdr, dot: "#22C55E" },
  belum_dibeli: { label: "Belum Dibeli", color: C.danger,  bg: C.dangerBg,  border: C.dangerBdr,  dot: "#EF4444" },
  // item-level statuses
  belum_beli:   { label: "Belum Dibeli", color: C.danger,  bg: C.dangerBg,  border: C.dangerBdr,  dot: "#EF4444" },
};

function getStatusCfg(status) {
  if (!status) return { label: "—", color: C.neutral, bg: C.neutralBg, border: C.neutralBdr, dot: "#94A3B8" };
  return STATUS_CFG[status.toLowerCase()] || { label: status, color: C.neutral, bg: C.neutralBg, border: C.neutralBdr, dot: "#94A3B8" };
}

function StatusChip({ value }) {
  const cfg = getStatusCfg(value);
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      px: "8px", py: "3px", backgroundColor: cfg.bg,
      border: `1px solid ${cfg.border}`, borderRadius: "6px",
    }}>
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: cfg.dot, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: cfg.color, lineHeight: 1 }}>
        {cfg.label}
      </Typography>
    </Box>
  );
}

/* ─── Sifat Chip ── */
const SIFAT_CFG = {
  biasa:    { label: "Biasa",    color: C.neutral, bg: C.neutralBg, border: C.neutralBdr },
  segera:   { label: "Segera",   color: C.warn,    bg: C.warnBg,    border: C.warnBdr    },
  mendesak: { label: "Mendesak", color: C.danger,  bg: C.dangerBg,  border: C.dangerBdr  },
};

function SifatChip({ value }) {
  if (!value) return <Typography sx={{ fontSize: "0.85rem", color: C.text1, fontWeight: 500 }}>—</Typography>;
  const cfg = SIFAT_CFG[value.toLowerCase()] || { label: value, color: C.neutral, bg: C.neutralBg, border: C.neutralBdr };
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center",
      px: "8px", py: "3px", backgroundColor: cfg.bg,
      border: `1px solid ${cfg.border}`, borderRadius: "6px",
    }}>
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: cfg.color, lineHeight: 1 }}>
        {cfg.label}
      </Typography>
    </Box>
  );
}

/* ─── Info Row ── */
function InfoRow({ icon, label, value, customValue }) {
  return (
    <Box sx={{
      display: "flex", alignItems: "flex-start", gap: 1.5, py: "11px",
      borderBottom: `1px solid ${C.border}`, "&:last-child": { borderBottom: "none", pb: 0 },
    }}>
      <Box sx={{ color: C.text3, mt: 0.1, flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ flexGrow: 1 }}>
        <Typography sx={{
          fontSize: "0.68rem", color: C.text3, mb: 0.3,
          textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700,
        }}>
          {label}
        </Typography>
        {customValue ?? (
          <Typography sx={{ fontSize: "0.85rem", color: C.text1, fontWeight: 500 }}>
            {value || "—"}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" });
}

/* ═══════════════════════════════════════════════════════════════════════════
   Main Component
═══════════════════════════════════════════════════════════════════════════ */
export default function DetailAtk() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  /* dialog tandai semua dibeli / belum dibeli */
  const [updating,       setUpdating]       = useState(null);
  const [approveOpen,    setApproveOpen]    = useState(false);
  const [rejectOpen,     setRejectOpen]     = useState(false);
  const [rejectNote,     setRejectNote]     = useState("");

  /* dialog per item */
  const [itemAction,   setItemAction]   = useState(null); // { type: 'dibeli'|'belum_beli', item }
  const [itemUpdating, setItemUpdating] = useState(null);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/atk/${id}`);
      setData(res.data?.data ?? res.data);
    } catch { setError("Gagal memuat detail pengajuan ATK."); }
    finally  { setLoading(false); }
  };

  useEffect(() => { fetchDetail(); }, [id]);

  /* ── Tandai semua dibeli ── */
  const handleApprove = async () => {
    try {
      setUpdating("dibeli");
      await api.put(`/atk/${id}/status`, { status: "dibeli" });
      setApproveOpen(false);
      await fetchDetail();
    } catch { setError("Gagal menandai semua item sebagai dibeli."); }
    finally  { setUpdating(null); }
  };

  /* ── Tandai semua belum dibeli ── */
  const handleReject = async () => {
    try {
      setUpdating("belum_dibeli");
      await api.put(`/atk/${id}/status`, { status: "belum_dibeli", note: rejectNote });
      setRejectOpen(false); setRejectNote("");
      await fetchDetail();
    } catch { setError("Gagal menandai semua item sebagai belum dibeli."); }
    finally  { setUpdating(null); }
  };

  /* ── Tandai per item: dibeli / belum_beli ── */
  const handleItemDibeli = async () => {
    if (!itemAction) return;
    try {
      setItemUpdating(itemAction.item.id);
      await api.post(`/atk/${id}/items/${itemAction.item.id}/approve`);
      setItemAction(null);
      await fetchDetail();
    } catch { setError("Gagal menandai item sebagai dibeli."); }
    finally  { setItemUpdating(null); }
  };

  const handleItemBelumBeli = async () => {
    if (!itemAction) return;
    try {
      setItemUpdating(itemAction.item.id);
      await api.post(`/atk/${id}/items/${itemAction.item.id}/reject`);
      setItemAction(null);
      await fetchDetail();
    } catch { setError("Gagal menandai item sebagai belum dibeli."); }
    finally  { setItemUpdating(null); }
  };

  /* ── Loading / Error ── */
  if (loading) return (
    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center",
      alignItems: "center", minHeight: 400, gap: 2 }}>
      <CircularProgress size={32} thickness={4} sx={{ color: C.primary }} />
      <Typography sx={{ fontSize: "0.82rem", color: C.text3 }}>Memuat data pengajuan ATK...</Typography>
    </Box>
  );

  if (error || !data) return (
    <Box sx={{ py: 8, textAlign: "center" }}>
      <Alert severity="error" sx={{ borderRadius: "8px", border: `1px solid ${C.dangerBdr}`, mb: 2 }}>
        {error || "Data tidak ditemukan."}
      </Alert>
      <Button onClick={() => navigate(-1)} variant="outlined"
        sx={{ borderRadius: "8px", textTransform: "none" }}>Kembali</Button>
    </Box>
  );

  const sc      = getStatusCfg(data.status?.name);
  const items   = data.items || [];
  const hasAnyPendingItem = items.some(i => (i.item_status || "pending") === "pending");

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F8FAFC", p: { xs: 2, md: 3 } }}>

      {/* ── Header ── */}
      <Box sx={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        mb: 3, flexWrap: "wrap", gap: 2,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton onClick={() => navigate(-1)} size="small" sx={{
            border: `1px solid ${C.border}`, borderRadius: "8px", width: 32, height: 32,
            color: C.text2, backgroundColor: C.surface,
            "&:hover": { borderColor: C.primary, color: C.primary, backgroundColor: C.primaryBg },
            transition: "all 0.15s",
          }}>
            <ArrowBackIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "10px", backgroundColor: sc.bg,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <EditNoteIcon sx={{ fontSize: 20, color: sc.color }} />
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: C.text1, lineHeight: 1.2 }}>
                  Detail Pengajuan ATK
                </Typography>
                <StatusChip value={data.status?.name} />
              </Box>
              <Typography sx={{
                fontSize: "0.75rem", color: C.text3, mt: 0.25,
                fontFamily: "'JetBrains Mono', monospace",
              }}>
                {data.request_number}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tombol aksi — tampil selama ada item pending */}
        {hasAnyPendingItem && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" size="small"
              startIcon={<CancelOutlinedIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() => setRejectOpen(true)} disabled={!!updating}
              sx={{
                height: 34, borderRadius: "8px", textTransform: "none", fontSize: "0.82rem",
                borderColor: C.dangerBdr, color: C.danger,
                "&:hover": { backgroundColor: C.dangerBg, borderColor: C.danger },
              }}>
              Belum Dibeli Semua
            </Button>
            <Button variant="contained" size="small"
              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: "14px !important" }} />}
              onClick={() => setApproveOpen(true)} disabled={!!updating}
              sx={{
                height: 34, borderRadius: "8px", textTransform: "none", fontSize: "0.82rem",
                fontWeight: 600, backgroundColor: C.success, boxShadow: "none",
                "&:hover": { backgroundColor: "#15803D", boxShadow: "none" },
              }}>
              Tandai Semua Dibeli
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}
          sx={{ mb: 2, borderRadius: "8px", border: `1px solid ${C.dangerBdr}` }}>
          {error}
        </Alert>
      )}

      {/* ── Body ── */}
      <Box sx={{ display: "flex", gap: 2.5, flexDirection: { xs: "column", md: "row" }, alignItems: "flex-start" }}>

        {/* ── Sidebar Kiri ── */}
        <Box sx={{ flex: "0 0 300px", display: "flex", flexDirection: "column", gap: 2 }}>

          {/* Info Pengajuan */}
          <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px", overflow: "hidden" }}>
            <Box sx={{ px: 2, py: "11px", borderBottom: `1px solid ${C.border}`, backgroundColor: C.surfaceAlt }}>
              <Typography sx={{
                fontSize: "0.72rem", fontWeight: 700, color: C.text3,
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>
                Informasi Pengajuan
              </Typography>
            </Box>
            <Box sx={{ px: 2, py: 1 }}>
              <InfoRow icon={<EditNoteIcon sx={{ fontSize: 15 }} />}           label="No. Pengajuan"     value={data.request_number} />
              <InfoRow icon={<CalendarTodayIcon sx={{ fontSize: 15 }} />}      label="Tanggal Diajukan"  value={formatDate(data.request_date)} />
              <InfoRow icon={<PersonOutlineIcon sx={{ fontSize: 15 }} />}      label="Nama Pemohon"      value={data.requester_name} />
              <InfoRow icon={<BusinessIcon sx={{ fontSize: 15 }} />}           label="Departemen / Unit" value={data.department} />
              <InfoRow icon={<AssignmentOutlinedIcon sx={{ fontSize: 15 }} />} label="Digunakan Untuk"   value={data.used_for} />
              <InfoRow icon={<PriorityHighIcon sx={{ fontSize: 15 }} />}       label="Sifat Permintaan"
                customValue={<SifatChip value={data.request_type} />} />
              <InfoRow icon={<PersonOutlineIcon sx={{ fontSize: 15 }} />}      label="Akun Pengaju"      value={data.user?.name} />
            </Box>
          </Paper>

          {/* Catatan */}
          {data.description && (
            <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px", p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <NotesIcon sx={{ fontSize: 15, color: C.text3 }} />
                <Typography sx={{
                  fontSize: "0.72rem", fontWeight: 700, color: C.text3,
                  textTransform: "uppercase", letterSpacing: "0.06em",
                }}>
                  Catatan Tambahan
                </Typography>
              </Box>
              <Typography sx={{ fontSize: "0.82rem", color: C.text2, lineHeight: 1.6 }}>
                {data.description}
              </Typography>
            </Paper>
          )}

          {/* Ringkasan */}
          <Paper elevation={0} sx={{
            border: `1px solid ${C.infoBdr}`, borderRadius: "12px", p: 2,
            background: `linear-gradient(135deg, ${C.primaryBg} 0%, #E0EEFF 100%)`,
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <Inventory2Icon sx={{ fontSize: 15, color: C.primary }} />
              <Typography sx={{
                fontSize: "0.72rem", fontWeight: 700, color: C.primary,
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>
                Ringkasan Item
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.78rem", color: C.text2 }}>Total Item</Typography>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: C.text1 }}>{items.length}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.78rem", color: C.success }}>Sudah Dibeli</Typography>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: C.success }}>
                {items.filter(i => i.item_status === "dibeli").length}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography sx={{ fontSize: "0.78rem", color: C.danger }}>Belum Dibeli</Typography>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: C.danger }}>
                {items.filter(i => i.item_status === "belum_beli").length}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography sx={{ fontSize: "0.78rem", color: C.warn }}>Pending</Typography>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: C.warn }}>
                {items.filter(i => !i.item_status || i.item_status === "pending").length}
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* ── Tabel Items ── */}
        <Paper elevation={0} sx={{
          border: `1px solid ${C.border}`, borderRadius: "12px",
          overflow: "hidden", flexGrow: 1,
        }}>
          <Box sx={{
            px: 2, py: "11px", borderBottom: `1px solid ${C.border}`,
            display: "flex", alignItems: "center", gap: 1, backgroundColor: C.surfaceAlt,
          }}>
            <EditNoteIcon sx={{ fontSize: 16, color: C.primary }} />
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: C.text1 }}>
              Daftar Item ATK
            </Typography>
            <Box sx={{
              display: "inline-flex", alignItems: "center",
              px: "7px", height: 20, backgroundColor: C.primaryBg,
              border: `1px solid ${C.infoBdr}`, borderRadius: "5px",
            }}>
              <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: C.primary }}>
                {items.length} item
              </Typography>
            </Box>
          </Box>

          <Box sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: C.surfaceAlt }}>
                  {["No", "Nama Item ATK", "Qty", "Satuan", "Status", "Aksi"].map(h => (
                    <TableCell key={h} sx={thSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, border: 0 }}>
                      <Typography sx={{ fontSize: "0.82rem", color: C.text3 }}>Tidak ada item</Typography>
                    </TableCell>
                  </TableRow>
                ) : items.map((item, idx) => {
                  const itemStatus       = item.item_status || "pending";
                  const isItemPending    = itemStatus === "pending";
                  const isProcessingThis = itemUpdating === item.id;

                  return (
                    <TableRow key={item.id || idx} sx={{
                      "&:hover": { backgroundColor: C.surfaceAlt },
                      transition: "background 0.12s",
                    }}>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: "0.72rem", color: C.text3, fontWeight: 600 }}>
                          {idx + 1}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: "0.85rem", color: C.text1, fontWeight: 600 }}>
                          {item.item_name || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Box sx={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          minWidth: 28, height: 22, px: 1,
                          borderRadius: "6px", backgroundColor: C.surfaceAlt, border: `1px solid ${C.border}`,
                        }}>
                          <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: C.text1 }}>
                            {item.quantity}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: "0.82rem", color: C.text2 }}>
                          {item.satuan || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <StatusChip value={itemStatus} />
                      </TableCell>
                      <TableCell sx={tdSx}>
                        {isItemPending ? (
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <Tooltip title="Tandai Dibeli" arrow>
                              <span>
                                <IconButton size="small"
                                  disabled={isProcessingThis}
                                  onClick={() => setItemAction({ type: "dibeli", item })}
                                  sx={{
                                    width: 28, height: 28, borderRadius: "6px",
                                    border: `1px solid ${C.successBdr}`,
                                    color: C.success, backgroundColor: C.successBg,
                                    "&:hover": { backgroundColor: "#DCFCE7" },
                                    "&:disabled": { opacity: 0.5 },
                                  }}>
                                  {isProcessingThis
                                    ? <CircularProgress size={12} sx={{ color: C.success }} />
                                    : <ShoppingCartIcon sx={{ fontSize: 14 }} />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Tandai Belum Dibeli" arrow>
                              <span>
                                <IconButton size="small"
                                  disabled={isProcessingThis}
                                  onClick={() => setItemAction({ type: "belum_beli", item })}
                                  sx={{
                                    width: 28, height: 28, borderRadius: "6px",
                                    border: `1px solid ${C.dangerBdr}`,
                                    color: C.danger, backgroundColor: C.dangerBg,
                                    "&:hover": { backgroundColor: "#FEE2E2" },
                                    "&:disabled": { opacity: 0.5 },
                                  }}>
                                  <RemoveShoppingCartIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: "0.72rem", color: C.text3 }}>—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>

      {/* ══ DIALOGS ══ */}

      {/* Dialog Tandai Semua Dibeli */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 380, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "24px 24px 0" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "10px", backgroundColor: C.successBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <CheckCircleIcon sx={{ fontSize: 20, color: C.success }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>
                Tandai Semua Item Dibeli?
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                Semua item pending pada pengajuan <strong>{data.request_number}</strong> akan ditandai sebagai <strong>Dibeli</strong>.
              </Typography>
            </Box>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={() => setApproveOpen(false)} disabled={!!updating}
            sx={{
              textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt },
            }}>
            Batal
          </Button>
          <Button onClick={handleApprove} disabled={!!updating} variant="contained"
            sx={{
              textTransform: "none", borderRadius: "8px", px: 2.5, fontWeight: 600,
              backgroundColor: C.success, boxShadow: "none",
              "&:hover": { backgroundColor: "#15803D", boxShadow: "none" },
            }}>
            {updating === "dibeli"
              ? <CircularProgress size={15} sx={{ color: "#fff" }} />
              : "Ya, Tandai Dibeli"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Tandai Semua Belum Dibeli */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "24px 24px 16px" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "10px", backgroundColor: C.dangerBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <WarningAmberIcon sx={{ fontSize: 20, color: C.danger }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>
                Tandai Semua Item Belum Dibeli?
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                Semua item pending pada pengajuan <strong>{data.request_number}</strong> akan ditandai sebagai <strong>Belum Dibeli</strong>.
              </Typography>
            </Box>
          </Box>
          <TextField fullWidth multiline rows={3} size="small"
            placeholder="Tulis catatan (opsional)..."
            value={rejectNote} onChange={(e) => setRejectNote(e.target.value)}
            sx={inputBase} />
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
          <Button onClick={() => { setRejectOpen(false); setRejectNote(""); }} disabled={!!updating}
            sx={{
              textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt },
            }}>
            Batal
          </Button>
          <Button onClick={handleReject} disabled={!!updating} variant="contained"
            sx={{
              textTransform: "none", borderRadius: "8px", px: 2.5, fontWeight: 600,
              backgroundColor: C.danger, boxShadow: "none",
              "&:hover": { backgroundColor: "#B91C1C", boxShadow: "none" },
            }}>
            {updating === "belum_dibeli"
              ? <CircularProgress size={15} sx={{ color: "#fff" }} />
              : "Ya, Belum Dibeli Semua"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Konfirmasi Tandai Dibeli (per item) */}
      <Dialog open={itemAction?.type === "dibeli"} onClose={() => setItemAction(null)}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 380, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "24px 24px 0" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "10px", backgroundColor: C.successBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <ShoppingCartIcon sx={{ fontSize: 20, color: C.success }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>
                Tandai Item Dibeli?
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                Item <strong>{itemAction?.item?.item_name}</strong> akan ditandai sebagai <strong>Dibeli</strong>.
              </Typography>
            </Box>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={() => setItemAction(null)} disabled={!!itemUpdating}
            sx={{
              textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt },
            }}>
            Batal
          </Button>
          <Button onClick={handleItemDibeli} disabled={!!itemUpdating} variant="contained"
            sx={{
              textTransform: "none", borderRadius: "8px", px: 2.5, fontWeight: 600,
              backgroundColor: C.success, boxShadow: "none",
              "&:hover": { backgroundColor: "#15803D", boxShadow: "none" },
            }}>
            {itemUpdating ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Ya, Dibeli"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Konfirmasi Tandai Belum Dibeli (per item) */}
      <Dialog open={itemAction?.type === "belum_beli"} onClose={() => setItemAction(null)}
        PaperProps={{ sx: { borderRadius: "14px", minWidth: 380, boxShadow: "0 20px 60px rgba(0,0,0,0.12)" } }}>
        <Box sx={{ p: "24px 24px 0" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <Box sx={{
              width: 40, height: 40, borderRadius: "10px", backgroundColor: C.dangerBg,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <RemoveShoppingCartIcon sx={{ fontSize: 20, color: C.danger }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: C.text1, mb: 0.5 }}>
                Tandai Item Belum Dibeli?
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: C.text2 }}>
                Item <strong>{itemAction?.item?.item_name}</strong> akan ditandai sebagai <strong>Belum Dibeli</strong>.
              </Typography>
            </Box>
          </Box>
        </Box>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button onClick={() => setItemAction(null)} disabled={!!itemUpdating}
            sx={{
              textTransform: "none", color: C.text2, fontWeight: 500,
              borderRadius: "8px", border: `1px solid ${C.border}`, px: 2,
              "&:hover": { backgroundColor: C.surfaceAlt },
            }}>
            Batal
          </Button>
          <Button onClick={handleItemBelumBeli} disabled={!!itemUpdating} variant="contained"
            sx={{
              textTransform: "none", borderRadius: "8px", px: 2.5, fontWeight: 600,
              backgroundColor: C.danger, boxShadow: "none",
              "&:hover": { backgroundColor: "#B91C1C", boxShadow: "none" },
            }}>
            {itemUpdating ? <CircularProgress size={15} sx={{ color: "#fff" }} /> : "Ya, Belum Dibeli"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
